export const VENDOR_COOKIE_PREFIXES = [
  "_ga",
  "_gid",
  "_clck",
  "_clsk",
  "_cl",
  "CLID",
  "ANONCHK",
  "MR",
  "MUID",
  "SM",
] as const;

const EXACT_VENDOR_COOKIES = new Set<string>([
  "CLID",
  "ANONCHK",
  "MR",
  "MUID",
  "SM",
]);

type TrackingWindow = Window & {
  gtag?: (...args: unknown[]) => void;
  clarity?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
};

export function buildCookieExpiryDomains(
  hostname: string,
): (string | undefined)[] {
  const domains: (string | undefined)[] = [undefined];

  if (!hostname) {
    return domains;
  }

  domains.push(hostname, `.${hostname}`);

  const labels = hostname.split(".");
  for (let i = 1; i <= labels.length - 2; i++) {
    const parent = labels.slice(i).join(".");
    domains.push(parent, `.${parent}`);
  }

  return [...new Set(domains)];
}

function matchesVendorCookie(name: string): boolean {
  if (EXACT_VENDOR_COOKIES.has(name)) {
    return true;
  }
  if (
    name === "_ga" ||
    name.startsWith("_ga_") ||
    name === "_gat" ||
    name.startsWith("_gat_")
  ) {
    return true;
  }
  if (name === "_gid" || name.startsWith("_gid_")) {
    return true;
  }
  if (name === "_clck" || name.startsWith("_clck_")) {
    return true;
  }
  if (name === "_clsk" || name.startsWith("_clsk_")) {
    return true;
  }
  if (name === "_cl") {
    return true;
  }
  return false;
}

function expireCookie(name: string, domain?: string): void {
  const domainPart = domain ? `; domain=${domain}` : "";
  const variants = ["", "; Secure; SameSite=None"];

  for (const securePart of variants) {
    document.cookie = `${name}=; path=/${domainPart}; max-age=0${securePart}`;
    document.cookie = `${name}=; path=/${domainPart}; expires=Thu, 01 Jan 1970 00:00:00 GMT${securePart}`;
  }
}

function clearVendorCookies(): void {
  const names = document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
    .filter((name): name is string => Boolean(name));

  const domains = buildCookieExpiryDomains(window.location.hostname);

  for (const name of names) {
    if (!matchesVendorCookie(name)) continue;
    for (const domain of domains) {
      expireCookie(name, domain);
    }
  }
}

export function stopVendorTracking(options: { marketingOnly?: boolean } = {}): void {
  const w = window as TrackingWindow;

  if (options.marketingOnly) {
    try {
      sessionStorage.removeItem("mot.marketing-click-ids.v1");
    } catch {
      // Storage may be unavailable; continue revoking active vendor state.
    }
    try {
      // Meta documents this consent command; Reddit and X have no equivalent
      // browser teardown API, so their loaders stop issuing future page views.
      if (typeof w.fbq === "function") {
        w.fbq("consent", "revoke");
      }
    } catch {
      // Fail closed when the vendor global is unavailable or throws.
    }
    return;
  }

  try {
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
      });
    }
  } catch {
    // Fail closed without throwing when gtag is unavailable or throws.
  }

  try {
    if (typeof w.clarity === "function") {
      w.clarity("stop");
    }
  } catch {
    // Fail closed without throwing when clarity is unavailable or throws.
  }

  try {
    clearVendorCookies();
  } catch {
    // Fail closed without throwing when cookie APIs are unavailable.
  }
}
